import React from 'react';
import useLocale from 'next-translate/useTranslation';
import { UserIcon, PencilIcon, VolumeUpIcon } from '@heroicons/react/outline';
import ButtonLink from '@/components/links/ButtonLink';
import { HAS_FREE_PLAN } from '@/constants/common';

interface Props {
  languageName: string;
  isAccent: boolean;
  languageFlag: string;
  /** Voice style pages (funny, scary, ...) — the language-specific copy
   * ("Input your text in Funny", "regional variations") doesn't apply. */
  isStyle?: boolean;
}

const HowToCreateSection = ({
  languageName,
  isAccent,
  languageFlag,
  isStyle = false,
}: Props) => {
  const { t } = useLocale('text-to-speech');

  const steps = [
    {
      icon: UserIcon,
      title: t('Find a voice'),
      description: t(
        'Create an account for free, choose a default voice, pick one from Voice Library, or clone your voice.',
      ),
    },
    {
      icon: PencilIcon,
      title: t('Enter text and adjust'),
      description: isStyle
        ? t(
            'Type or paste your script — any language works, and the {{language}} voice will perform it in that style.',
            { language: t(languageName) },
          )
        : isAccent
          ? t(
              'Input English text. The model understands which language you are using and will generate audio accordingly.',
            )
          : t(
              'Input your text in {{language}}. The model understands the language and will generate audio accordingly.',
              { language: t(languageName) },
            ),
    },
    {
      icon: VolumeUpIcon,
      title: t('Generate audio'),
      description: isStyle
        ? t('Press generate and download the mp3 audio file.')
        : isAccent
          ? t(
              'Press generate and download the {{accent}} accent mp3 audio file.',
              { accent: t(languageName) },
            )
          : t('Press generate and download the {{language}} mp3 audio file.', {
              language: t(languageName),
            }),
    },
  ];

  const features = [
    {
      title: isStyle
        ? t('{{language}} voices with real character', {
            language: t(languageName),
          })
        : isAccent
          ? t('Authentic {{accent}} accent nuances', {
              accent: t(languageName),
            })
          : t('Authentic {{language}} nuances', { language: t(languageName) }),
      description: isStyle
        ? t(
            'Our {{language}} voice generator uses AI to produce expressive speech with genuine personality — delivery that fits your script, from short clips to full narrations.',
            { language: t(languageName) },
          )
        : isAccent
          ? t(
              "Our {{accent}} accent voice generator leverages AI to produce speech that's both clear and culturally resonant. It captures the unique aspects of the English language, including its regional dialects.",
              { accent: t(languageName) },
            )
          : t(
              "Our {{language}} voice generator leverages AI to produce speech that's both clear and culturally resonant. It captures the unique aspects of the {{language}} language, including its regional variations.",
              { language: t(languageName) },
            ),
    },
    {
      title: t('Contextual Awareness'),
      description: isStyle
        ? t(
            'The AI understands the context of your text, ensuring the delivery always fits the moment.',
          )
        : isAccent
          ? t(
              'Our {{accent}} accent AI model understands the context of the text, ensuring that the generated audio is always relevant and appropriate.',
              { accent: t(languageName) },
            )
          : t(
              'Our {{language}} AI model understands the context of the text, ensuring that the generated audio is always relevant and appropriate.',
              { language: t(languageName) },
            ),
    },
    {
      title: t('Natural Pauses'),
      description: t(
        'Full stops, commas, and other punctuation are automatically detected and translated into natural pauses.',
      ),
    },
  ];

  return (
    <div className='bg-white py-16 dark:bg-slate-900 sm:py-24'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        {/* How to create section */}
        <div className='text-center'>
          <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
            {isAccent
              ? t('How to create {{accent}} accent text to speech', {
                  accent: t(languageName),
                })
              : t('How to create {{language}} text to speech', {
                  language: t(languageName),
                })}
          </h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-gray-500 dark:text-gray-400'>
            {t(
              'Follow these simple steps to generate high-quality audio in just minutes.',
            )}
          </p>
        </div>

        {/* Steps */}
        <div className='mt-16'>
          <div className='grid grid-cols-1 gap-8 md:grid-cols-3'>
            {steps.map((step, index) => (
              <div
                key={index}
                className='relative rounded-3xl bg-white dark:bg-slate-800 p-8 shadow-lg ring-1 ring-gray-900/5 dark:ring-white/10 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 text-center'
              >
                <div className='mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 ring-1 ring-inset ring-primary-500/20'>
                  <step.icon className='h-8 w-8' aria-hidden='true' />
                </div>
                <h3 className='mt-6 text-xl font-semibold text-gray-900 dark:text-white'>
                  {step.title}
                </h3>
                <p className='mt-4 text-base text-gray-600 dark:text-slate-300'>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Features section */}
        <div className='mt-24'>
          <div className='text-center mb-16'>
            <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
              <span className='mr-3 text-4xl'>{languageFlag}</span>
              {isAccent
                ? t('{{accent}} Accent Voice Generator', {
                    accent: t(languageName),
                  })
                : t('{{language}} Voice Generator', {
                    language: t(languageName),
                  })}
            </h2>
          </div>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
            {features.map((feature, index) => (
              <div
                key={index}
                className='rounded-3xl p-8 bg-gray-50 dark:bg-slate-800/50 hover:bg-white dark:hover:bg-slate-800 ring-1 ring-gray-900/5 dark:ring-white/5 shadow-sm hover:shadow-md transition-all duration-300'
              >
                <h3 className='text-xl font-semibold text-gray-900 dark:text-white'>
                  {feature.title}
                </h3>
                <p className='mt-4 text-base text-gray-600 dark:text-slate-300'>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className='mt-24 text-center'>
          <div className='relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-50 to-purple-50 px-6 py-16 dark:from-primary-900/20 dark:to-purple-900/20 ring-1 ring-primary-500/10 sm:px-12'>
            <div className='absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary-500/10 blur-3xl' />
            <div className='absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-purple-500/10 blur-3xl' />
            <div className='relative z-10'>
              <h3 className='text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl'>
                {isAccent
                  ? t('Ready to create {{accent}} accent speech?', {
                      accent: t(languageName),
                    })
                  : t('Ready to create {{language}} speech?', {
                      language: t(languageName),
                    })}
              </h3>
              <p className='mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-slate-300'>
                {t(
                  'Start generating high-quality audio with our AI voice generator. No credit card required.',
                )}
              </p>
              <div className='mt-10 flex justify-center'>
                <ButtonLink
                  href={HAS_FREE_PLAN ? `/tts` : '/#pricing'}
                  variant='dark'
                  className='group relative overflow-hidden rounded-full shadow-lg hover:scale-105 transition-all duration-200'
                >
                  <span className='relative flex items-center gap-2'>
                    {t('Start creating for free')}
                    <svg
                      className='h-4 w-4 transition-transform group-hover:translate-x-1'
                      fill='none'
                      viewBox='0 0 24 24'
                      strokeWidth='2'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3'
                      />
                    </svg>
                  </span>
                </ButtonLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowToCreateSection;
